import type { UciEngine } from './uci-engine.js';

export interface ServerEngineOpts {
  /** Base URL of the API. Empty string (default) means same-origin (dev proxy); set a VITE_API_URL for cross-origin. */
  readonly baseUrl?: string;
}

/**
 * Bridges the api server (which runs Stockfish in-process) to the UciEngine
 * interface over fetch + SSE. `position`/`setoption` are buffered locally and
 * shipped with each `go` request; the server streams back UCI lines as SSE
 * `data:` events. All line parsing stays in UciClient/ChessAI.
 */
export function createServerEngine(opts: ServerEngineOpts = {}): UciEngine {
  const base = opts.baseUrl ?? '';
  let onMsg: ((l: string) => void) | null = null;
  let fen = '';
  const options = new Map<string, string>();
  let goArgs = '';
  let inFlight: AbortController | null = null;

  async function analyse(): Promise<void> {
    const ctl = new AbortController();
    inFlight = ctl;
    try {
      const res = await fetch(`${base}/api/ai/go`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fen, options: Object.fromEntries(options), goArgs }),
        signal: ctl.signal,
      });
      if (!res.ok || !res.body) throw new Error(`engine request failed: ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
        let idx = buffer.indexOf('\n\n');
        while (idx !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          for (const dataLine of frame.split('\n')) {
            if (dataLine.startsWith('data: ')) onMsg?.(dataLine.slice(6));
          }
          idx = buffer.indexOf('\n\n');
        }
      }
    } catch (err) {
      if (!ctl.signal.aborted) {
        onMsg?.(`error: ${err instanceof Error ? err.message : 'engine request failed'}`);
      }
    } finally {
      // Only clear our own slot: a superseded fetch (aborted on retry) must not
      // null out the newer request's controller.
      if (inFlight === ctl) inFlight = null;
    }
  }

  return {
    postMessage(line: string): void {
      const cmd = line.trim();
      if (cmd === 'uci') {
        queueMicrotask(() => {
          onMsg?.('id name S4K Server');
          onMsg?.('uciok');
        });
        return;
      }
      if (cmd === 'isready') {
        queueMicrotask(() => onMsg?.('readyok'));
        return;
      }
      if (cmd === 'quit') {
        inFlight?.abort();
        return;
      }
      if (cmd.startsWith('position fen ')) {
        fen = cmd.slice('position fen '.length);
        return;
      }
      if (cmd.startsWith('setoption name ')) {
        const rest = cmd.slice('setoption name '.length);
        const eq = rest.indexOf(' value ');
        if (eq !== -1) options.set(rest.slice(0, eq), rest.slice(eq + 7));
        return;
      }
      if (cmd.startsWith('go ')) {
        goArgs = cmd.slice(3);
        // A retry supersedes any still-streaming request (ChessAI askWithRetry
        // re-posts go after a timeout); abort it so its late lines don't resolve
        // the new ask with stale data.
        inFlight?.abort();
        void analyse();
      }
    },
    onMessage(cb: (l: string) => void): void {
      onMsg = cb;
    },
    terminate(): void {
      inFlight?.abort();
    },
  };
}
